import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, FileText, Clock, User, Calendar } from 'lucide-react';
import './Approvals.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Approvals = ({ currentUser }) => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (currentUser?.email) {
      fetchPendingApprovals();
    }
  }, [currentUser]);

  const fetchPendingApprovals = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/approvals/pending-approvals/${currentUser.email}`
      );
      const data = await response.json();
      setPendingApprovals(data.pending_approvals || []);
    } catch (error) {
      console.error('Error fetching approvals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprovalAction = async (action) => {
    if (!selectedApproval) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/approve-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: selectedApproval.submission_id,
          approver_email: currentUser.email,
          action: action,
          comments: comments
        })
      });

      const result = await response.json();
      
      if (action === 'accept') {
        alert(`✅ Approved! ${result.message}\n\nNotifications sent to:\n- Aaron Gregory\n- Lindsay Schneider`);
      } else {
        alert(`❌ Rejected. Notifications sent to submitter and leadership.`);
      }

      // Refresh pending approvals
      setSelectedApproval(null);
      setComments('');
      fetchPendingApprovals();

    } catch (error) {
      console.error('Error processing approval:', error);
      alert('Failed to process approval. Please try again.');
    }
  };

  if (isLoading) {
    return <div className="approvals-loading">Loading approvals...</div>;
  }

  // Check if user is an approver (Madison or Donovan)
  const isApprover = currentUser?.email === 'MadisonBailey.President@agwesternrail.com' ||
                     currentUser?.email === 'DonovanJones.vicepresident@agwesternrail.com';

  if (!isApprover) {
    return (
      <div className="approvals-empty">
        <FileText size={48} />
        <h3>Approval Access</h3>
        <p>This section is only available to authorized approvers (President & Vice President).</p>
      </div>
    );
  }

  return (
    <div className="approvals-container">
      <div className="approvals-header">
        <h2>Pending Approvals</h2>
        <div className="approvals-count">
          <Clock size={20} />
          <span>{pendingApprovals.length} pending</span>
        </div>
      </div>

      {pendingApprovals.length === 0 ? (
        <div className="approvals-empty">
          <CheckCircle size={48} />
          <h3>All Caught Up!</h3>
          <p>You have no pending approvals at this time.</p>
        </div>
      ) : (
        <div className="approvals-list">
          {pendingApprovals.map((approval) => (
            <div
              key={approval.submission_id}
              className="approval-card"
              onClick={() => setSelectedApproval(approval)}
            >
              <div className="approval-icon">
                <FileText size={24} />
              </div>
              <div className="approval-info">
                <h4>{approval.form_title}</h4>
                <p className="approval-submitter">
                  <User size={14} /> Submitted by: {approval.submitted_by}
                </p>
                <p className="approval-date">
                  <Calendar size={14} /> {new Date(approval.submission_date).toLocaleString()}
                </p>
              </div>
              <button className="review-btn">Review</button>
            </div>
          ))}
        </div>
      )}

      {/* Approval Modal */}
      {selectedApproval && (
        <div className="approval-modal-overlay">
          <div className="approval-modal">
            <div className="approval-modal-header">
              <h3>Review: {selectedApproval.form_title}</h3>
              <button onClick={() => setSelectedApproval(null)}>×</button>
            </div>

            <div className="approval-modal-body">
              <div className="approval-detail-section">
                <h4>Submission Details</h4>
                <div className="approval-details">
                  <p><strong>Submitted by:</strong> {selectedApproval.submitted_by}</p>
                  <p><strong>Email:</strong> {selectedApproval.submitted_by_email}</p>
                  <p><strong>Date:</strong> {new Date(selectedApproval.submission_date).toLocaleString()}</p>
                  <p><strong>Status:</strong> <span className="status-badge">{selectedApproval.status}</span></p>
                </div>
              </div>

              <div className="approval-detail-section">
                <h4>Form Data</h4>
                <div className="form-data-display">
                  {Object.entries(selectedApproval.form_data).map(([key, value]) => (
                    <div key={key} className="form-data-row">
                      <span className="form-data-key">{key}:</span>
                      <span className="form-data-value">{value || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="approval-detail-section">
                <h4>Electronic Signature</h4>
                <div className="signature-display">
                  <img src={selectedApproval.signature} alt="Signature" />
                </div>
              </div>

              <div className="approval-detail-section">
                <h4>Comments (Optional)</h4>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add any comments or notes..."
                  rows="3"
                />
              </div>
            </div>

            <div className="approval-modal-footer">
              <button
                className="reject-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to reject this request?')) {
                    handleApprovalAction('reject');
                  }
                }}
              >
                <XCircle size={18} /> Reject
              </button>
              <button
                className="accept-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to approve this request?')) {
                    handleApprovalAction('accept');
                  }
                }}
              >
                <CheckCircle size={18} /> Accept & Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approvals;
